import {
  getGetVerificationCodeBatchesQueryKey,
  PaginatedVerificationCodeBatchListItemsDTO,
  useDeleteBatchById,
  useGetVerificationCodeBatches,
} from "@bvhi/backend-api";
import { Card, CardContent, GridPaginationModel, GridSortModel } from "@bvhi/ui-base";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { forwardRef, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { BVHIDataGrid } from "../../../../../../components/datagrid/bvhi-datagrid";
import { getAppConfig } from "../../../../../../config/config";
import { SecuredDeleteDialog } from "../../../../../../dialogs/secured-delete-dialog";
import { useDownload } from "../../../../../../hooks/use-download";
import { useToast } from "../../../../../../hooks/use-toast";
import { Route } from "../../../../../../routes/_main/app/verification-codes.index";
import { narrowTypeByField } from "../../../../../../utils/ts";
import { useVerificationBatchSortParams } from "../../hooks/use-verification-batch-sort-params";
import { useVerificationCodesDataGridDefinitions } from "../../hooks/use-verification-codes-datagrid-definitions";

interface Props {
  updateCallback: (paginationModel: GridPaginationModel, sortModel: GridSortModel) => void;
}

export const VerificationCodesDataGrid = forwardRef<HTMLDivElement, Props>(({ updateCallback }, ref) => {
  const searchParams = Route.useSearch();
  const { t } = useTranslation(["verification-codes", "dialogs"]);
  const { getSortModelForBackendString } = useVerificationBatchSortParams();
  const queryClient = useQueryClient();
  const { mutate: deleteBatchById } = useDeleteBatchById();

  const [verificationCodeIdToDelete, setVerificationCodeIdToDelete] = useState<string | null>(null);
  const { pushDeleteSuccessToast, pushDeleteErrorToast } = useToast();
  const { fetchFileAndDownload } = useDownload();

  const { data, isFetching } = useGetVerificationCodeBatches(searchParams, {
    query: { throwOnError: (error) => error.status === 500 || error.status === 403, placeholderData: keepPreviousData },
  });

  const handleDelete = useCallback(
    (userId: string) => {
      setVerificationCodeIdToDelete(userId);
    },
    [setVerificationCodeIdToDelete]
  );

  const handleDownload = (id: string) => {
    void fetchFileAndDownload(`${getAppConfig().env.baseUrl}/gateway/platform/v1/verification-codes/batches/${id}/download`);
  };

  const { columns } = useVerificationCodesDataGridDefinitions(handleDelete, handleDownload);

  const handleCornfirmDialogClose = useCallback(
    (confirmed: boolean) => {
      if (!confirmed || !verificationCodeIdToDelete) {
        setVerificationCodeIdToDelete(null);
        return;
      }
      deleteBatchById(
        { batchId: verificationCodeIdToDelete },
        {
          onSuccess: () => {
            pushDeleteSuccessToast(t("verification.codes.verification.codes"));
          },
          onError: () => {
            pushDeleteErrorToast(t("verification.codes.verification.codes"));
          },
          onSettled: () => {
            setVerificationCodeIdToDelete(null);
            void queryClient.invalidateQueries({ queryKey: getGetVerificationCodeBatchesQueryKey() });
          },
        }
      );
    },
    [deleteBatchById, pushDeleteErrorToast, pushDeleteSuccessToast, queryClient, t, verificationCodeIdToDelete]
  );

  const batches = data?.data;
  if (!narrowTypeByField<PaginatedVerificationCodeBatchListItemsDTO>(batches, "payload")) return;

  return (
    <div ref={ref}>
      <Card>
        <CardContent restrictContentWidth={false}>
          <BVHIDataGrid
            columns={columns}
            initialSortModel={getSortModelForBackendString(searchParams.sort && searchParams.sort.length >= 1 ? searchParams.sort[0] : "")}
            loading={isFetching}
            rowCount={batches.numberOfItems ?? -1}
            rows={batches.payload ?? []}
            totalPages={batches.numberOfPages ?? 0}
            updateCallback={updateCallback}
            initalPaginationModel={{
              page: searchParams.page ? searchParams.page : 0,
              pageSize: searchParams.size ? searchParams.size : 25,
            }}
          />
        </CardContent>
      </Card>
      <SecuredDeleteDialog
        onClose={handleCornfirmDialogClose}
        open={verificationCodeIdToDelete !== null}
        overwrite={{ title: t("dialogs:dialogs.delete.verification.code.title") }}
      />
    </div>
  );
});
VerificationCodesDataGrid.displayName = "VerificationCodesDataGrid";
